import { Trans, useLingui } from '@lingui/react/macro';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@pantry/ui';
import { useCurrentUser, useUpdateCurrentUser, type MeasurementSystem } from '@pantry/api-client';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '@pantry/shared';
import { toast } from 'sonner';
import {
  AdjustmentsHorizontalIcon,
  ScaleIcon,
  SunIcon,
  LanguageIcon,
} from '@heroicons/react/24/outline';

export function PreferencesSection() {
  const { t } = useLingui();
  const { data: user } = useCurrentUser();
  const updateUser = useUpdateCurrentUser();

  // Define measurement options inside component so t() macro works for extraction
  const measurementOptions: { value: MeasurementSystem; label: string; description: string }[] = [
    {
      value: 'metric',
      label: t({ message: 'Metric', context: 'measurement_system' }),
      description: t`Kilograms, liters, milliliters`,
    },
    {
      value: 'imperial',
      label: t({ message: 'Imperial', context: 'measurement_system' }),
      description: t`Pounds, ounces, cups, fluid ounces`,
    },
  ];

  const handleMeasurementChange = async (value: string) => {
    try {
      await updateUser.mutateAsync({
        measurementSystem: value as MeasurementSystem,
      });
      toast.success(t`Measurement system updated`);
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to update preferences`;
      toast.error(message);
    }
  };

  const handleLanguageChange = async (value: string) => {
    try {
      await updateUser.mutateAsync({
        language: value as LanguageCode,
      });
      toast.success(t`Language preference updated`);
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to update language`;
      toast.error(message);
    }
  };

  const currentSystem = user?.measurementSystem ?? 'metric';
  const currentLanguage = user?.language ?? 'en';

  return (
    <Card className="rounded-xl" data-tour="settings-preferences">
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
            <AdjustmentsHorizontalIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <Trans>Preferences</Trans>
        </CardTitle>
        <CardDescription>
          <Trans>Customize your app experience.</Trans>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Measurement System */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ScaleIcon className="h-4 w-4 text-muted-foreground" />
            <div>
              <Label className="text-base">
                <Trans>Measurement System</Trans>
              </Label>
              <p className="text-sm text-muted-foreground">
                <Trans>Choose how quantities are displayed throughout the app.</Trans>
              </p>
            </div>
          </div>
          <RadioGroup
            value={currentSystem}
            onValueChange={handleMeasurementChange}
            disabled={updateUser.isPending}
            className="grid gap-3"
          >
            {measurementOptions.map((option) => (
              <label
                key={option.value}
                htmlFor={`measurement-${option.value}`}
                className={`flex items-start space-x-3 rounded-xl border p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                  currentSystem === option.value ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <RadioGroupItem
                  value={option.value}
                  id={`measurement-${option.value}`}
                  className="mt-0.5"
                />
                <div className="grid gap-0.5">
                  <span className="font-medium text-foreground">{option.label}</span>
                  <p className="text-sm text-muted-foreground">{option.description}</p>
                </div>
              </label>
            ))}
          </RadioGroup>
        </div>

        {/* Language Preference */}
        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-center gap-2">
            <LanguageIcon className="h-4 w-4 text-muted-foreground" />
            <div>
              <Label className="text-base">
                <Trans>Language</Trans>
              </Label>
              <p className="text-sm text-muted-foreground">
                <Trans>
                  Choose your preferred language for product names and the app interface.
                </Trans>
              </p>
            </div>
          </div>
          <select
            value={currentLanguage}
            onChange={(e) => handleLanguageChange(e.target.value)}
            disabled={updateUser.isPending}
            className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border disabled:cursor-not-allowed disabled:opacity-50"
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            <Trans>
              Product names from Open Food Facts will be displayed in your preferred language when
              available.
            </Trans>
          </p>
        </div>

        {/* Theme Section - informational only since theme is in header */}
        <div className="space-y-2 pt-4 border-t">
          <div className="flex items-center gap-2">
            <SunIcon className="h-4 w-4 text-muted-foreground" />
            <div>
              <Label className="text-base">
                <Trans>Theme</Trans>
              </Label>
              <p className="text-sm text-muted-foreground">
                <Trans>You can change your theme using the toggle in the header menu.</Trans>
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
